import { type JSX, useEffect, useRef, useState } from 'react';
import Box from 'carbon-react/esm/components/box';
import DsnTable from './component/DsnTable';
import PreviewListComponent from '../../components/PreviewList';
import { useDsnListState } from '../../hooks/useDsnListState';
import FilterLayout from './layouts/FilterLayout';
import FilterLayoutProps from './types/filterlayout.types';
import { Typography } from 'carbon-react/esm/components/typography/__internal__/__next__/typography.component';
import { clearDsnListReturnState, readDsnListReturnState } from '../../utils/dsnListReturnState';

const DsnList = (): JSX.Element => {
  const [returnState] = useState(readDsnListReturnState);
  const {
    data,
    isDataLoading,
    pagination,
    localStartDate,
    localEndDate,
    localSelectedNatures,
    localSelectedStatus,
    localIsTest,
    searchValue,
    handleFilterChange,
    handleUserSelect,
    handleDateRangeChange,
    handleNatureChange,
    handleStatusChange,
    handleModeChange,
    handleSearch,
    handleSubmitFilters,
    handlePaginationChange,
    localSelectedUserIds,
    selectedDatePreset,
    handleDatePresetChange,
    sort,
    handleSort,
  } = useDsnListState(
    returnState
      ? {
          currentPage: returnState.currentPage,
          pageSize: returnState.pageSize,
        }
      : undefined,
  );

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const hasRestoredListPosition = useRef(false);

  useEffect(() => {
    if (
      !data ||
      isDataLoading ||
      !returnState ||
      hasRestoredListPosition.current ||
      pagination.currentPage !== returnState.currentPage ||
      pagination.pageSize !== returnState.pageSize
    ) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      window.scrollTo({
        top: returnState.scrollY,
        left: 0,
        behavior: 'auto',
      });
      clearDsnListReturnState();
      hasRestoredListPosition.current = true;
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [data, isDataLoading, pagination.currentPage, pagination.pageSize, returnState]);

  const handleSelectedRowsChange = (newSelectedRows: Record<string, boolean>) => {
    setSelectedRows(newSelectedRows);
  };

  const filterProps: FilterLayoutProps = {
    startDate: localStartDate,
    endDate: localEndDate,
    selectedNatures: localSelectedNatures,
    onFilterChange: handleFilterChange,
    onSelectChange: handleUserSelect,
    onDateRangeChange: handleDateRangeChange,
    onNatureChange: handleNatureChange,
    selectedUserIdState: localSelectedUserIds,
    onSubmitFilters: handleSubmitFilters,
    selectedStatus: localSelectedStatus,
    isTest: localIsTest,
    onStatusChange: handleStatusChange,
    onModeChange: handleModeChange,
    onSearch: handleSearch,
    searchValue: searchValue ?? '',
    selectedDatePreset: selectedDatePreset,
    onDatePresetChange: handleDatePresetChange,
  };

  return (
    <Box display="flex" flex="1" flexDirection="column">
      <Box pt={24} pb={24} pl={16} pr={16} borderRadius="borderRadius000">
        <Typography variant="h1">Déclaration sociale nominative (DSN)</Typography>
      </Box>

      <Box>
        <Box>
          <FilterLayout {...filterProps} selectedRows={selectedRows} data={data?.value || []} />
        </Box>
      </Box>

      <Box display="flex" flex="1" flexDirection="column" pt={24} pb={24} pl={16} pr={16}>
        {isDataLoading || !data ? (
          <PreviewListComponent />
        ) : (
          <DsnTable
            data={data.value}
            totalCount={data['@odata.count'] ?? 0}
            onPaginationChange={handlePaginationChange}
            currentPage={pagination.currentPage}
            pageSize={pagination.pageSize}
            initialActiveRowId={returnState?.selectedRowId}
            onSelectedRowsChange={handleSelectedRowsChange}
            sort={sort}
            onSort={handleSort}
          />
        )}
      </Box>
    </Box>
  );
};

export default DsnList;
